/**
 * THE SOURCE OF TRUTH — every business fact the site can state.
 *
 * Phase 0 found `Parker@veepo.ca` hardcoded in eight places across five files.
 * Nothing may be hardcoded again. Every page, every schema block, every
 * generated artefact (sitemap, robots.txt, llms.txt) reads from here.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE HONESTY CONTRACT (Operating Brief, Iron Law 5)
 *
 * Anything this file cannot state as verified truth is typed `null` and the
 * UI omits the block entirely. There are no placeholder values that look real.
 * A `null` here is a visible gap, which is correct — a fabricated value would
 * be an invisible lie that an answer engine would then repeat to a prospect.
 *
 * Fields marked NEEDS-OWNER-INPUT are commercial representations. Fill them
 * once, here, and every surface updates together.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const BUSINESS = {
  /** Brand name as it appears in the title tag and schema. */
  name: "Fly4MEdia",
  /** Legal entity name. NEEDS-OWNER-INPUT if it differs from `name`. */
  legalName: null as string | null,
  domain: "https://fly4me.ca",

  tagline: "Aerial photography and drone cinematography",

  email: "Parker@veepo.ca",

  /** Headquarters. Street address is NEEDS-OWNER-INPUT — omitted until supplied. */
  address: {
    streetAddress: null as string | null,
    addressLocality: "Calgary",
    addressRegion: "AB",
    postalCode: null as string | null,
    addressCountry: "CA",
  },

  /** Calgary base — used for drive-time maths and the coverage map origin. */
  geo: { latitude: 51.0447, longitude: -114.0719 },

  sameAs: [
    "https://instagram.com/fly4media",
    "https://vimeo.com/fly4media",
    "https://youtube.com/@fly4media",
    // NEEDS-OWNER-INPUT: Google Business Profile URL. Top-3 local-pack factor.
  ],

  priceRange: "$$",
} as const;

/**
 * CREDENTIALS — confirmed by the owner on 2026-08-31.
 *
 * `rpasCertification: "Advanced"` is load-bearing. Under Transport Canada's
 * RPAS rules an Advanced certificate is what permits operations in controlled
 * airspace. Because most of Calgary sits inside the CYYC control zone and the
 * west side sits under CYBW, this single fact is the difference between a
 * service area and a brochure. It is asserted on every location page.
 *
 * Anything still `null` is not claimed anywhere on the site.
 */
export const CREDENTIALS = {
  rpasCertification: "Advanced" as "Basic" | "Advanced" | null,
  /** NEEDS-OWNER-INPUT — carrier name and coverage amount. */
  liabilityInsurance: null as { carrier: string; coverageCad: number } | null,
  /** NEEDS-OWNER-INPUT — Transport Canada drone registration number(s). */
  droneRegistration: null as string[] | null,
} as const;

/**
 * COVERAGE — confirmed by the owner on 2026-08-31.
 *
 * "We have shot in BC, and all over Canada, but our headquarters is in
 * Calgary, Alberta." That sentence defines three concentric truths, and the
 * whole geo architecture exists to make all three machine-legible:
 *
 *   home      — flown routinely from the Calgary base
 *   national  — real prior work, mobilised per engagement
 *   global    — available worldwide on a client-funded travel basis
 */
export const COVERAGE = {
  homeProvince: "Alberta",
  /** Provinces with confirmed prior work. */
  provincesWorked: ["Alberta", "British Columbia"] as const,
  nationalCoverage: true,
  internationalCoverage: true,
} as const;

/**
 * THE TRAVEL MODEL — structure confirmed, figures deliberately absent.
 *
 * The owner confirmed the commercial model: the client covers all expenses.
 * That is stated plainly and completely below, because it is true and because
 * candour about money is this cluster's strongest conversion lever.
 *
 * What is NOT stated is any specific rate, deposit percentage, or lead-time
 * guarantee. Those are commercial representations. A fabricated "$2,400/day,
 * 50% deposit, 14-day lead time" published here would be quoted verbatim by
 * ChatGPT and Perplexity to real prospects, and the business would be holding
 * a promise it never made. Quote-based copy converts just as well and cannot
 * be contradicted.
 *
 * Fill `dayRateCad`, `depositPercent`, `minimumEngagementDays` and
 * `leadTimeDays` here when the real numbers exist; the copy and the schema
 * `Offer` blocks pick them up automatically and the quote-based fallback
 * language retires itself.
 */
export const TRAVEL_MODEL = {
  /** Everything the client covers. Verified structure — safe to publish. */
  expensesCovered: [
    "Return airfare from Calgary for the crew",
    "Accommodation for every night on the ground",
    "Ground transport at the destination",
    "Local flight permits and airspace authorisations",
    "Any destination-specific insurance rider",
    "Equipment shipping or excess-baggage fees where the kit cannot fly as carry-on",
  ],
  /** What the fee itself buys — the service, not the travel. */
  feeCovers: [
    "Pre-flight airspace and regulatory research for the destination",
    "Shoot days on site",
    "Post-production and colour",
    "Digital delivery of the finished files",
  ],

  // ── NEEDS-OWNER-INPUT — quote-based copy renders until these are filled ──
  dayRateCad: null as number | null,
  depositPercent: null as number | null,
  minimumEngagementDays: null as number | null,
  leadTimeDays: null as number | null,

  /** Regions the business declines. Empty = no published exclusions. */
  regionsDeclined: [] as string[],
} as const;

/**
 * SERVICES — the taxonomy every page links into.
 * Names must match what the business actually sells.
 */
export const SERVICES = [
  {
    slug: "aerial-photography",
    name: "Aerial photography",
    short: "Stills from altitude — property, land, site and campaign imagery.",
  },
  {
    slug: "aerial-cinematography",
    name: "Aerial cinematography",
    short: "Moving-camera work: orbits, reveals, push-ins, tracking passes.",
  },
  {
    slug: "real-estate-media",
    name: "Real estate media",
    short: "Listing packages that show lot, approach, light and neighbourhood context.",
  },
  {
    slug: "fpv-drone-filming",
    name: "FPV drone filming",
    short: "Single-take flythroughs that a conventional airframe cannot fly.",
  },
] as const;

/** Every place the site states a response commitment. One string, one truth. */
export const RESPONSE_COMMITMENT = "Reply within one business day";

/** Convenience: is a credential claimable in copy and schema? */
export const hasAdvancedCert = CREDENTIALS.rpasCertification === "Advanced";

/**
 * THE CAPABILITY STATEMENT — what a procurement team, an advisory firm or a
 * consulting engagement manager needs before they can raise a purchase order.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS SEPARATELY FROM THE MARKETING COPY
 *
 * A consultancy subcontracting aerial capture for a client engagement is not
 * asking the questions a homeowner asks. They are asking: are you certified,
 * are you insured, will you sign our NDA, who owns the footage, how do you
 * handle client-confidential site data, can you cover multiple provinces on one
 * engagement, and can you produce the same deliverable in month nine that you
 * produced in month one.
 *
 * Almost no aerial company publishes those answers, so the questions get asked
 * over email and the vendor who answers fastest wins. Publishing them makes the
 * business findable by an answer engine when a consultant asks "who can do
 * aerial capture across Alberta for a client project?" — which is precisely the
 * query this cluster is built to be cited for.
 *
 * Anything unconfirmed stays null and the page omits it. Iron Law 5 applies to
 * procurement claims exactly as it applies to testimonials.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const CAPABILITY = {
  /** Confirmed. Advanced permits controlled-airspace operation. */
  certification: "Transport Canada RPAS Pilot Certificate — Advanced Operations",

  /** Geographic reach for a single engagement. */
  engagementReach: [
    "Alberta and British Columbia as home territory, mobilised from Calgary",
    "The rest of Canada on a per-engagement basis, with confirmed prior work",
    "International on a client-funded travel basis",
  ],

  /** What a repeat or multi-site programme can hold constant. */
  repeatability: [
    "Fixed coordinates, altitude and heading recorded per capture point",
    "Identical framing reproducible across months or years",
    "Flight logs retained per sortie, timestamped and location-stamped",
    "Consistent deliverable specification across every site in a programme",
  ],

  /** Commercial and data-handling posture. */
  engagementTerms: [
    "Work is quoted per engagement against a written scope",
    "Non-disclosure agreements accepted — client paper or ours",
    "Raw files available to the client on request, not held hostage",
    "Site imagery treated as client-confidential unless release is agreed",
  ],

  /** Deliverables a professional engagement can specify. */
  deliverables: [
    "High-resolution stills, colour-managed",
    "Edited video in the format the end use requires",
    "Repeat-capture sequences for progress or condition monitoring",
    "Raw files where the engagement calls for them",
  ],

  // ── NEEDS-OWNER-INPUT — omitted from every page until supplied ──────────
  /** Procurement asks for this on almost every vendor onboarding form. */
  wcbCoverage: null as string | null,
  /** Business number / GST registration for invoicing. */
  businessNumber: null as string | null,
  /** Standard payment terms, if a published one exists. */
  paymentTerms: null as string | null,
} as const;

/**
 * The buyer types this business genuinely serves. Used for `audience` in schema
 * and to shape the enterprise cluster's copy — not a wish list.
 */
export const AUDIENCES = [
  {
    slug: "consultancies",
    name: "Advisory and consulting firms",
    note: "Subcontracted aerial capture for a client engagement, delivered to the consultancy's specification and under its confidentiality terms.",
  },
  {
    slug: "engineering-environmental",
    name: "Engineering and environmental consultants",
    note: "Site condition, access and boundary imagery for assessment work, captured without putting a person at height.",
  },
  {
    slug: "developers-construction",
    name: "Developers and construction",
    note: "Repeat progress capture from fixed coordinates, so a sequence reads as one continuous record rather than unrelated photographs.",
  },
  {
    slug: "corporate-real-estate",
    name: "Corporate real estate and asset owners",
    note: "Portfolio condition imagery and site verification across multiple properties on one engagement.",
  },
  {
    slug: "brokerages-marketing",
    name: "Brokerages and marketing teams",
    note: "Listing and campaign media at volume, with consistent treatment across a portfolio.",
  },
] as const;
